"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatePicker = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var moment_1 = tslib_1.__importDefault(require("moment"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var i18n_1 = require("../i18n");
var controlled_1 = require("../form/controlled");
var CalendarPart_1 = tslib_1.__importDefault(require("../calendar/CalendarPart"));
var CalendarTable_1 = require("../calendar/CalendarTable");
var TimeSupportWrapper_1 = require("./TimeSupportWrapper");
var Input_1 = require("../input/Input");
var RangePicker_1 = require("./RangePicker");
var util_1 = require("../timepicker/util");
var dropdown_1 = require("../dropdown");
var MonthPicker_1 = require("./MonthPicker");
var util_2 = require("./util");
var popover_1 = require("../popover");
var use_default_1 = require("../_util/use-default");
var config_context_1 = require("../_util/config-context");
var noop_1 = require("../_util/noop");
var icon_1 = require("../icon");
var forward_ref_with_statics_1 = require("../_util/forward-ref-with-statics");
var merge_event_props_1 = require("../_util/merge-event-props");
var getFormat = function (showTime) { return (showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD"); };
exports.DatePicker = forward_ref_with_statics_1.forwardRefWithStatics(function DatePicker(props, ref) {
    var t = i18n_1.useTranslation(moment_1.default);
    var classPrefix = config_context_1.useConfig().classPrefix;
    var _a = controlled_1.useDefaultValue(props), header = _a.header, className = _a.className, style = _a.style, showTime = _a.showTime, value = _a.value, onChange = _a.onChange, disabled = _a.disabled, _format = _a.format, _b = _a.placeholder, placeholder = _b === void 0 ? showTime ? t.selectTime : t.selectDate : _b, _c = _a.defaultOpen, defaultOpen = _c === void 0 ? false : _c, open = _a.open, _d = _a.onOpenChange, onOpenChange = _d === void 0 ? noop_1.noop : _d, _e = _a.placement, placement = _e === void 0 ? "bottom-start" : _e, _f = _a.placementOffset, placementOffset = _f === void 0 ? 5 : _f, _g = _a.closeOnScroll, closeOnScroll = _g === void 0 ? true : _g, escapeWithReference = _a.escapeWithReference, popupContainer = _a.popupContainer, overlayClassName = _a.overlayClassName, overlayStyle = _a.overlayStyle, clearable = _a.clearable, restProps = tslib_1.__rest(_a, ["header", "className", "style", "showTime", "value", "onChange", "disabled", "format", "placeholder", "defaultOpen", "open", "onOpenChange", "placement", "placementOffset", "closeOnScroll", "escapeWithReference", "popupContainer", "overlayClassName", "overlayStyle", "clearable"]);
    var _h = tslib_1.__read(react_1.useState(false), 2), hover = _h[0], setHover = _h[1];
    var format = _format || getFormat(showTime);
    // 当前面板类型
    var _j = tslib_1.__read(react_1.useState("date"), 2), type = _j[0], setType = _j[1];
    // 当前面板展示时间
    var _k = tslib_1.__read(react_1.useState(moment_1.default.isMoment(value) ? value : getDefaultViewMoment()), 2), curViewMoment = _k[0], setCurViewMoment = _k[1];
    // 当前选中日期
    var _l = tslib_1.__read(react_1.useState(value), 2), curValue = _l[0], setCurValue = _l[1];
    // 上次选中日期
    var preValidValueRef = react_1.useRef(value);
    // 选择器是否展开
    var _m = tslib_1.__read(use_default_1.useDefault(open, defaultOpen, onOpenChange), 2), active = _m[0], setActive = _m[1];
    react_1.useEffect(function () {
        setCurValue(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);
    // 输入框显示值
    var inputRef = react_1.useRef(null);
    var getInputValue = react_1.useCallback(function (value) {
        return moment_1.default.isMoment(value)
            ? value.locale(t.locale).format(format)
            : "";
    }, [t.locale, format]);
    var _o = tslib_1.__read(react_1.useState(getInputValue(curValue)), 2), inputValue = _o[0], setInputValue = _o[1];
    react_1.useEffect(function () {
        setCurValue(value);
        setInputValue(getInputValue(value));
    }, [format, value, getInputValue]);
    react_1.useEffect(function () {
        preValidValueRef.current = value;
    }, [value]);
    function handleChange(value, context) {
        // 同步日期/时间
        if (showTime && moment_1.default.isMoment(value)) {
            value = syncDate(value, context.type); // eslint-disable-line no-param-reassign
        }
        setCurValue(value);
        setInputValue(getInputValue(value));
        // 没有时间选择时没有二次确认选中
        if (!showTime) {
            onChange(value, context);
            handleClose();
        }
    }
    function handleOk(event) {
        onChange(curValue, { event: event });
        handleClose();
    }
    function handleOpen() {
        if (disabled) {
            return;
        }
        setActive(true);
        setType("date");
    }
    function handleClose() {
        setInputValue(getInputValue(value));
        setActive(false);
    }
    /**
     * 同步日期/时间
     * 如果当前修改为日期，则同步上次时间并修正；
     * 如果当前修改为时间，则同步上次日期；
     */
    function syncDate(value, type) {
        var preValidValue = preValidValueRef.current;
        var range = props.range, _a = props.disabledTime, disabledTime = _a === void 0 ? function () { return ({}); } : _a;
        // 如果包含上次选择，则以上次选择为基准同步
        if (moment_1.default.isMoment(preValidValue)) {
            if (type === "date") {
                var time = util_1.getValidTimeValue(preValidValue, tslib_1.__assign(tslib_1.__assign({ range: CalendarTable_1.getTimeRange(value, range) }, disabledTime(value)), { hourStep: (showTime === null || showTime === void 0 ? void 0 : showTime.hourStep) || 1, minuteStep: (showTime === null || showTime === void 0 ? void 0 : showTime.minuteStep) || 1, secondStep: (showTime === null || showTime === void 0 ? void 0 : showTime.secondStep) || 1 }));
                value.set(util_1.getHourMinuteSecond(time));
            }
            if (type === "time") {
                value.set(util_2.getYearMonthDate(preValidValue));
            }
            // 如果是首次选择，则将当前时间进行修正
        }
        else if (type === "date") {
            // 合并 showTime.defaultValue
            if (moment_1.default.isMoment(showTime === null || showTime === void 0 ? void 0 : showTime.defaultValue)) {
                value.set(util_1.getHourMinuteSecond(showTime.defaultValue));
            }
            var time = util_1.getValidTimeValue(value, tslib_1.__assign(tslib_1.__assign({ range: CalendarTable_1.getTimeRange(value, range) }, disabledTime(value)), { hourStep: (showTime === null || showTime === void 0 ? void 0 : showTime.hourStep) || 1, minuteStep: (showTime === null || showTime === void 0 ? void 0 : showTime.minuteStep) || 1, secondStep: (showTime === null || showTime === void 0 ? void 0 : showTime.secondStep) || 1 }));
            value.set(util_1.getHourMinuteSecond(time));
        }
        preValidValueRef.current = value;
        return value;
    }
    /**
     * 获取面板默认展示时间
     */
    function getDefaultViewMoment() {
        var range = props.range;
        var time = showTime && typeof showTime === "object"
            ? showTime.defaultValue
            : undefined;
        var m = null;
        if (!time) {
            m = moment_1.default();
        }
        else {
            m = moment_1.default(util_1.getHourMinuteSecond(time));
        }
        // range 判断
        if (Array.isArray(range)) {
            var _a = tslib_1.__read(range, 2), start = _a[0], end = _a[1];
            if (!moment_1.default.isMoment(start)) {
                start = moment_1.default(0);
            }
            if (!moment_1.default.isMoment(end)) {
                end = moment_1.default(Math.pow(2, 52));
            }
            if (util_2.isBefore(end, m, "month")) {
                return end;
            }
            if (util_2.isAfter(start, m, "month")) {
                return start;
            }
        }
        return m;
    }
    return (react_1.default.createElement(popover_1.Popover, { trigger: [
            util_2.DatePickerTrigger,
            { onOpen: handleOpen, onClose: handleClose },
        ], visible: active, onVisibleChange: setActive, placement: placement, placementOffset: placementOffset, closeOnScroll: closeOnScroll, escapeWithReference: escapeWithReference, popupContainer: popupContainer, overlayClassName: overlayClassName, overlayStyle: overlayStyle, overlay: react_1.default.createElement(dropdown_1.DropdownBox, { preventMouseDown: true },
            react_1.default.createElement(CalendarPart_1.default.Panel, { timeMode: type === "time" },
                !!header && react_1.default.createElement(CalendarPart_1.default.Header, null, header),
                react_1.default.createElement(TimeSupportWrapper_1.TimeSupportWrapper, tslib_1.__assign({}, restProps, { showTime: showTime, value: curValue, onChange: handleChange, onOk: handleOk, type: type, onTypeChange: function (type) { return setType(type); } }), function (props) { return (react_1.default.createElement(CalendarTable_1.CalendarTable, tslib_1.__assign({}, props, { curViewMoment: curViewMoment, onCurViewMomentChange: setCurViewMoment }))); }))) },
        react_1.default.createElement("div", tslib_1.__assign({}, ref, { className: classnames_1.default(classPrefix + "-datepicker", className), style: style }, merge_event_props_1.mergeEventProps(props)),
            react_1.default.createElement("div", { className: classnames_1.default(classPrefix + "-datepicker__input", {
                    "is-hover": hover && value && clearable,
                }), onMouseEnter: function () { return setHover(true); }, onMouseLeave: function () { return setHover(false); } },
                react_1.default.createElement(Input_1.Input, { ref: inputRef, disabled: disabled, placeholder: placeholder, value: inputValue, onFocus: function () { return inputRef.current.blur(); } }),
                clearable && !disabled && (react_1.default.createElement(icon_1.Icon, { type: "dismiss", onClick: function (event) {
                        event.stopPropagation();
                        onChange(null, { event: event });
                    } }))))));
}, {
    RangePicker: RangePicker_1.RangePicker,
    MonthPicker: MonthPicker_1.MonthPicker,
    defaultLabelAlign: "middle",
});
exports.DatePicker.displayName = "DatePicker";
//# sourceMappingURL=DatePicker.js.map